import React from "react";
import { ChecklistQuestion } from "../../../data-model/medicalNecessityReview";
import { getRecommendation } from "../utils/statementInjector";

interface ReviewChecklistProps {
  questions: ChecklistQuestion[];
  currentAnswers: Record<string, any>;
  onAnswerChange: (questionId: string, answer: any) => void;
  orderId: string;
  recommendation?: "approved" | "denial" | "review_required";
  recommendationText?: string;
}

const ReviewChecklist: React.FC<ReviewChecklistProps> = ({
  questions,
  currentAnswers,
  onAnswerChange,
  orderId,
  recommendation: propRecommendation,
  recommendationText: propRecommendationText,
}) => {
  // Get recommendation from statement mappings if not provided in props
  const recommendation = getRecommendation(orderId);

  const recommendationType =
    propRecommendation ||
    (recommendation && typeof recommendation === "object"
      ? recommendation.type
      : undefined);
  const recommendationMessage =
    propRecommendationText ||
    (recommendation && typeof recommendation === "object"
      ? recommendation.text
      : "") ||
    "";

  // Render recommendation banner
  const renderRecommendationBanner = () => {
    if (!recommendationMessage) return null;

    const isApproved = recommendationType === "approved";
    const isDenial = recommendationType === "denial";

    const bgColor = isApproved
      ? "bg-green-50"
      : isDenial
        ? "bg-red-50"
        : "bg-yellow-50";
    const borderColor = isApproved
      ? "border-green-200"
      : isDenial
        ? "border-red-200"
        : "border-yellow-200";
    const textColor = isApproved
      ? "text-green-700"
      : isDenial
        ? "text-red-700"
        : "text-yellow-700";
    const icon = isApproved ? "✓" : isDenial ? "⚠" : "ℹ";

    return (
      <div
        className={`mb-6 flex items-center gap-2 rounded-lg border ${borderColor} ${bgColor} px-4 py-3`}
      >
        <span className={`text-lg ${textColor}`}>{icon}</span>
        <div className={`flex-1 ${textColor}`}>
          <div className="text-sm">{recommendationMessage}</div>
        </div>
      </div>
    );
  };

  // Render a single question
  const renderQuestion = (question: ChecklistQuestion, depth: number = 0) => {
    const currentAnswer = currentAnswers[question.id];
    const hasAnswer = currentAnswer !== undefined && currentAnswer !== null;

    return (
      <div
        key={question.id}
        className={`question-item mb-8 ${depth > 0 ? "ml-6 border-l-2 border-gray-300 pl-6" : ""}`}
      >
        {/* Question Text */}
        <div className="mb-4">
          <label className="text-base font-bold leading-normal text-primaryGray-1">
            {question.question}
            {question.required && (
              <span className="ml-1 text-tertiaryRed-form_error">*</span>
            )}
          </label>
        </div>

        {/* Question Options */}
        {question.question_type === "single_correct" && (
          <div className="space-y-3">
            {question.options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-start gap-3"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={currentAnswer === option.value}
                  onChange={(e) => onAnswerChange(question.id, e.target.value)}
                  className="mt-0.5 h-4 w-4 cursor-pointer text-blue-600"
                />
                <span className="flex-1 text-sm leading-normal text-primaryGray-1">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        )}

        {question.question_type === "multi_correct" && (
          <div className="space-y-2">
            {question.options.map((option) => {
              const selectedValues = Array.isArray(currentAnswer)
                ? currentAnswer
                : [];
              const isChecked = selectedValues.includes(option.value);

              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = isChecked
                        ? selectedValues.filter((v) => v !== option.value)
                        : [...selectedValues, option.value];
                      onAnswerChange(question.id, newValues);
                    }}
                    className="mt-1"
                  />
                  <span className="flex-1 text-gray-700">{option.label}</span>
                </label>
              );
            })}
          </div>
        )}

        {question.question_type === "subjective" && (
          <textarea
            value={currentAnswer || ""}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className="w-full rounded-md border border-gray-300 p-3"
            rows={4}
            placeholder="Enter your response..."
          />
        )}

        {/* Supportive/Contradictory Statements */}
        {hasAnswer && question.facts && (
          <div className="mt-6 space-y-4">
            {question.facts.supportive_facts &&
              question.facts.supportive_facts.length > 0 && (
                <div className="rounded-lg border border-tertiaryGreen-50 bg-tertiaryGreen-50 p-4">
                  <div className="mb-3 font-bold leading-normal text-tertiaryGreen-700">
                    Supportive Statement
                  </div>
                  <ul className="list-disc space-y-2 pl-5 text-sm leading-normal text-primaryGray-2">
                    {question.facts.supportive_facts.map((fact, index) => (
                      <li key={index}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}

            {question.facts.contradictory_facts &&
              question.facts.contradictory_facts.length > 0 && (
                <div className="rounded-lg border border-secondaryYellow-11 bg-secondaryYellow-11 p-4">
                  <div className="mb-3 text-small font-semiBold leading-small text-secondaryYellow-1">
                    Contradictory Statement
                  </div>
                  <ul className="list-disc space-y-2 pl-5 text-small leading-small text-primaryGray-2">
                    {question.facts.contradictory_facts.map((fact, index) => (
                      <li key={index}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        )}

        {/* Render nested children based on answer */}
        {question.children &&
          currentAnswer &&
          question.children[currentAnswer] && (
            <div className="mt-4">
              {question.children[currentAnswer].map((childQuestion) =>
                renderQuestion(childQuestion, depth + 1),
              )}
            </div>
          )}
      </div>
    );
  };

  return (
    <div className="review-checklist">
      {/* Recommendation Banner */}
      {renderRecommendationBanner()}

      {/* Section Header - Removed as questions already have clear titles */}

      {/* Questions */}
      <div className="questions-container">
        {questions && questions.length > 0 ? (
          questions.map((question) => renderQuestion(question))
        ) : (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-center text-gray-500">
            No questions available for this order.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewChecklist;
